import {
  AlertCircle,
  AlertTriangle,
  MessageSquareOff,
  MessageSquareWarning,
  Settings,
} from 'lucide-react';

import { pluralize } from '@/portainer/helpers/strings';

import { PageHeader } from '@@/PageHeader';
import { Tab, WidgetTabs, useCurrentTabIndex } from '@@/Widget/WidgetTabs';
import { Badge } from '@@/Badge';
import { Icon } from '@@/Icon';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';

import { ActiveAlertsDatatable } from './ActiveAlertsDatatable/ActiveAlertsDatatable';
import { SilencedAlertsDatatable } from './SilencedAlertsDatatable/SilencedAlertsDatatable';
import { useActiveAlerts } from './ActiveAlertsDatatable/useActiveAlerts';
import {
  ActiveAlertsResponse,
  AlertSeverity,
} from './ActiveAlertsDatatable/types';
import { AlertSettingsDatatable } from './AlertSettingsDatatable/AlertSettingsDatatable';
import { AlertRulesDatatable } from './AlertRulesDatatable/AlertRulesDatatable';

export function AlertingView() {
  const alertCountQuery = useActiveAlerts({
    select: getAlertCountBySeverity,
  });
  const tabs: Tab[] = [
    {
      name: (
        <>
          Active Alerts
          {alertCountQuery.data?.warning || alertCountQuery.data?.critical ? (
            <div className="ml-2 flex gap-1">
              {alertCountQuery.data?.warning ? (
                <TooltipWithChildren
                  position="top"
                  message={`${
                    alertCountQuery.data.critical
                  } warning ${pluralize(
                    alertCountQuery.data.critical,
                    'alert'
                  )}`}
                >
                  <Badge className="flex-none" type="warn">
                    <Icon icon={AlertTriangle} className="!mr-1" />
                    {alertCountQuery.data.warning}
                  </Badge>
                </TooltipWithChildren>
              ) : null}
              {alertCountQuery.data?.critical ? (
                <TooltipWithChildren
                  position="top"
                  message={`${
                    alertCountQuery.data.critical
                  } critical ${pluralize(
                    alertCountQuery.data.critical,
                    'alert'
                  )}`}
                >
                  <Badge className="flex-none" type="danger">
                    <Icon icon={AlertCircle} className="!mr-1" />
                    {alertCountQuery.data.critical}
                  </Badge>
                </TooltipWithChildren>
              ) : null}
            </div>
          ) : null}
        </>
      ),
      icon: MessageSquareWarning,
      widget: <ActiveAlertsDatatable />,
      selectedTabParam: 'active-alerts',
    },
    {
      name: 'Silenced Alerts',
      icon: MessageSquareOff,
      widget: <SilencedAlertsDatatable />,
      selectedTabParam: 'alert-silences',
    },
    {
      name: 'Rules',
      icon: AlertCircle,
      widget: <AlertRulesDatatable />,
      selectedTabParam: 'rules',
    },
    {
      name: 'Settings',
      icon: Settings,
      widget: <AlertSettingsDatatable />,
      selectedTabParam: 'settings',
    },
  ];
  const currentTabIndex = useCurrentTabIndex(tabs);

  return (
    <>
      <PageHeader title="Alerting" reload />

      <>
        <WidgetTabs tabs={tabs} currentTabIndex={currentTabIndex} />
        <div className="content">{tabs[currentTabIndex].widget}</div>
      </>
    </>
  );
}

type AlertRuleCountBySeverity = Record<AlertSeverity, number>;

function getAlertCountBySeverity(
  alertsResponse: ActiveAlertsResponse
): AlertRuleCountBySeverity {
  const alertCountBySeverity: AlertRuleCountBySeverity = {
    critical: 0,
    warning: 0,
  };

  // Count alerts from all payloads
  alertsResponse.forEach((payload) => {
    if (payload.Payload && Array.isArray(payload.Payload)) {
      payload.Payload.forEach((alert) => {
        if (alert.labels.severity) {
          alertCountBySeverity[alert.labels.severity]++;
        }
      });
    }
  });

  return alertCountBySeverity;
}
